/**
 * 💬 TXTOFS - Salons Texte + Photos
 * 
 * Alternative aux salons vocaux pour ceux qui préfèrent écrire !
 * - Chat en temps réel avec API backend
 * - Partage de photos
 * - Emojis et réactions
 * - Ambiances thématiques
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Image, Send, Smile, Heart, Users, 
  Camera, X, ChevronLeft, Sparkles, Coffee, Moon,
  Music, Gamepad2, BookOpen, Utensils, Plane, Star,
  Loader2, RefreshCw, ImagePlus, Trash2, Video, Play, Pause
} from 'lucide-react';
import { Button } from '../../ui/button';
import { GuideAvatar } from '../soul/GuideAvatar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 😊 Réactions disponibles
const REACTIONS = ['❤️', '😂', '😮', '😢', '🔥', '👏', '💯', '🥰'];

// Icônes par catégorie
const CATEGORY_ICONS = {
  chill: Coffee,
  night: Moon,
  music: Music,
  gaming: Gamepad2,
  culture: BookOpen,
  food: Utensils,
  travel: Plane,
  love: Heart,
};

/**
 * 🎬 VideoMessage - Lecteur vidéo inline pour les messages
 */
function VideoMessage({ videoUrl, duration, isDarkMode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  return (
    <div 
      className="relative mb-2 rounded-xl overflow-hidden max-w-[280px] cursor-pointer group"
      onClick={togglePlay}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => !isPlaying && setShowControls(true)}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-auto max-h-48 object-cover rounded-xl"
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        playsInline
        loop={false}
      />
      
      {/* Overlay avec bouton play/pause */}
      <AnimatePresence>
        {(showControls || !isPlaying) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/30 flex items-center justify-center"
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isPlaying 
                  ? 'bg-white/20' 
                  : 'bg-gradient-to-r from-pink-500 to-purple-500'
              }`}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white ml-1" />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Badge vidéo + durée */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-full">
        <Video className="w-3 h-3 text-white" />
        <span className="text-xs text-white font-medium">
          {duration ? `${duration}s` : 'Vidéo'}
        </span>
      </div>
    </div>
  );
}

/**
 * 💬 TxtofsTab - Onglet principal des salons texte
 */
export function TxtofsTab({ customer, profile, isDarkMode }) {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les salons depuis l'API
  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/txtofs/rooms`);
      const data = await response.json();
      
      if (data.success) {
        setRooms(data.rooms);
        setError(null);
      } else {
        setError('Erreur lors du chargement des salons');
      }
    } catch (err) {
      console.error('Erreur fetch rooms:', err);
      setError('Impossible de charger les salons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  if (selectedRoom) {
    return (
      <TxtofRoom
        room={selectedRoom}
        customer={customer}
        profile={profile}
        isDarkMode={isDarkMode}
        onBack={() => {
          setSelectedRoom(null);
          fetchRooms(); // Rafraîchir la liste au retour
        }}
      />
    );
  }
  
  return (
    <div className={`space-y-4 min-h-[500px] ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="tab-text-rooms">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Salons Txtofs 💬
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Texte & photos • Discute sans micro !
          </p>
        </div>
        <button
          onClick={fetchRooms}
          className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          title="Rafraîchir"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        </button>
      </div>
      
      {/* Guide Pixie */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-purple-900/30 border-purple-500/30' : 'bg-purple-50 border-purple-200'} border`}>
        <div className="flex items-center gap-3">
          <GuideAvatar guide="pixie" expression="matchmaker_wink" size="md" />
          <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            <span className="font-bold text-pink-400">Pixie :</span> Pas envie de parler ? Les Txtofs c'est fait pour toi ! 
            Texte, photos, emojis... Exprime-toi comme tu veux ! 💬✨
          </p>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
          <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Chargement des salons...
          </span>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600'}`}>
          <p>{error}</p>
          <Button onClick={fetchRooms} variant="outline" className="mt-2">
            Réessayer
          </Button>
        </div>
      )}
      
      {/* Liste des salons */}
      {!loading && !error && (
        <div className="grid gap-3">
          {rooms.map((room) => {
            const IconComponent = CATEGORY_ICONS[room.category] || MessageCircle;
            return (
              <motion.button
                key={room.id}
                onClick={() => setSelectedRoom(room)}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  isDarkMode 
                    ? 'bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700' 
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
                }`}
                whileHover={{ scale: 1.01, x: 4 }}
                whileTap={{ scale: 0.99 }}
                data-testid={`room-${room.id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Icône */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${room.color} flex items-center justify-center text-2xl`}>
                    {room.emoji}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {room.name}
                      </h3>
                      {room.onlineCount > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
                          <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                          {room.onlineCount} en ligne
                        </span>
                      )}
                    </div>
                    <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {room.description}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Users className="w-3 h-3 inline mr-1" />
                        {room.messagesCount || 0} messages
                      </span>
                      {room.lastMessage && (
                        <span className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          💬 {room.lastMessage}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Flèche */}
                  <ChevronLeft className={`w-5 h-5 rotate-180 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * 💬 TxtofRoom - Interface de chat d'un salon
 */
function TxtofRoom({ room, customer, profile, isDarkMode, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageTimeRef = useRef(null);

  const customerId = customer?.id;
  const displayName = profile?.displayName || customer?.firstName || 'Anonyme';
  const avatar = profile?.avatar || '😊';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Charger les messages
  const fetchMessages = useCallback(async (isPolling = false) => {
    try {
      if (!isPolling) setLoading(true);
      
      const response = await fetch(`${API_URL}/api/txtofs/rooms/${room.id}/messages?limit=50`);
      const data = await response.json();
      
      if (data.success) {
        setMessages(data.messages);
        if (data.messages.length > 0) {
          lastMessageTimeRef.current = data.messages[data.messages.length - 1].createdAt;
        }
      }
    } catch (err) {
      console.error('Erreur fetch messages:', err);
      if (!isPolling) toast.error('Erreur lors du chargement des messages');
    } finally {
      if (!isPolling) setLoading(false);
    }
  }, [room.id]);

  // Polling pour les nouveaux messages
  const pollNewMessages = useCallback(async () => {
    if (!lastMessageTimeRef.current) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/txtofs/rooms/${room.id}/poll?since=${encodeURIComponent(lastMessageTimeRef.current)}`
      );
      const data = await response.json();
      
      if (data.success && data.messages.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
          if (newMsgs.length > 0) {
            lastMessageTimeRef.current = data.messages[data.messages.length - 1].createdAt;
            return [...prev, ...newMsgs];
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Erreur polling:', err);
    }
  }, [room.id]);

  // Rejoindre le salon au montage
  useEffect(() => {
    const joinRoom = async () => {
      try {
        await fetch(`${API_URL}/api/txtofs/rooms/${room.id}/join?customerId=${customerId}`, {
          method: 'POST'
        });
      } catch (err) {
        console.error('Erreur join room:', err);
      }
    };

    fetchMessages();
    if (customerId) joinRoom();

    // Démarrer le polling
    pollIntervalRef.current = setInterval(pollNewMessages, 3000);

    return () => {
      // Quitter le salon au démontage
      if (customerId) {
        fetch(`${API_URL}/api/txtofs/rooms/${room.id}/leave?customerId=${customerId}`, {
          method: 'POST'
        }).catch(() => {});
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [room.id, customerId, fetchMessages, pollNewMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Envoyer un message texte
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/txtofs/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.id,
          customerId: customerId,
          text: newMessage.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessages(prev => [...prev, { ...data.message, isMe: true }]);
        setNewMessage('');
        lastMessageTimeRef.current = data.message.createdAt;
      } else {
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Erreur send message:', err);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  // Envoyer une photo
  const handleSendPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Valider le type
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }

    // Valider la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convertir en base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageBase64 = event.target.result;
        
        const response = await fetch(`${API_URL}/api/txtofs/messages/send-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId: room.id,
            customerId: customerId,
            imageBase64: imageBase64,
            caption: null
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setMessages(prev => [...prev, { ...data.message, isMe: true }]);
          lastMessageTimeRef.current = data.message.createdAt;
          toast.success('📸 Photo envoyée !');
        } else {
          toast.error(data.detail || 'Erreur lors de l\'envoi de la photo');
        }
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        toast.error('Erreur lors de la lecture de l\'image');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Erreur send photo:', err);
      toast.error('Erreur lors de l\'envoi de la photo');
      setUploadingPhoto(false);
    }
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 🎬 Envoyer une vidéo courte (max 30 secondes)
  const handleSendVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Valider le type
    if (!file.type.startsWith('video/')) {
      toast.error('Seules les vidéos sont acceptées');
      return;
    }

    // Valider la taille (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Vidéo trop lourde (max 50MB)');
      return;
    }

    // Vérifier la durée de la vidéo
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(video.src);
      
      // Limiter à 30 secondes
      if (video.duration > 30) {
        toast.error('🎬 Vidéo trop longue ! Maximum 30 secondes');
        if (videoInputRef.current) videoInputRef.current.value = '';
        return;
      }

      setUploadingVideo(true);
      setVideoProgress(0);
      
      try {
        // Lire le fichier en base64
        const reader = new FileReader();
        
        reader.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 50);
            setVideoProgress(progress);
          }
        };
        
        reader.onload = async (event) => {
          setVideoProgress(60);
          const videoBase64 = event.target.result;
          
          try {
            setVideoProgress(70);
            const response = await fetch(`${API_URL}/api/txtofs/messages/send-video`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                roomId: room.id,
                customerId: customerId,
                videoBase64: videoBase64,
                duration: Math.round(video.duration),
                caption: null
              })
            });
            
            setVideoProgress(90);
            const data = await response.json();
            
            if (data.success) {
              setMessages(prev => [...prev, { ...data.message, isMe: true }]);
              lastMessageTimeRef.current = data.message.createdAt;
              toast.success('🎬 Vidéo envoyée !');
              setVideoProgress(100);
            } else {
              toast.error(data.detail || 'Erreur lors de l\'envoi de la vidéo');
            }
          } catch (err) {
            console.error('Erreur upload vidéo:', err);
            toast.error('Erreur lors de l\'envoi de la vidéo');
          }
          
          setUploadingVideo(false);
          setVideoProgress(0);
        };
        
        reader.onerror = () => {
          toast.error('Erreur lors de la lecture de la vidéo');
          setUploadingVideo(false);
          setVideoProgress(0);
        };
        
        reader.readAsDataURL(file);
      } catch (err) {
        console.error('Erreur send video:', err);
        toast.error('Erreur lors de l\'envoi de la vidéo');
        setUploadingVideo(false);
        setVideoProgress(0);
      }
    };
    
    video.onerror = () => {
      toast.error('Impossible de lire cette vidéo');
    };
    
    video.src = URL.createObjectURL(file);
    
    // Reset file input
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  // Ajouter une réaction
  const handleAddReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(`${API_URL}/api/txtofs/messages/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageId: messageId,
          customerId: customerId,
          emoji: emoji
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, reactions: data.reactions } : msg
        ));
      }
    } catch (err) {
      console.error('Erreur reaction:', err);
    }
  };

  // Supprimer un message
  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/txtofs/messages/${messageId}?customerId=${customerId}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        toast.success('Message supprimé');
      } else {
        toast.error(data.detail || 'Impossible de supprimer');
      }
    } catch (err) {
      console.error('Erreur delete:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className={`flex flex-col h-[500px] ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className={`flex items-center gap-3 p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={onBack}
          className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          data-testid="back-button"
        >
          <ChevronLeft className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
        </button>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${room.color} flex items-center justify-center text-xl`}>
          {room.emoji}
        </div>
        <div className="flex-1">
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{room.name}</h3>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {messages.length} messages
          </p>
        </div>
      </div>
      
      {/* Messages */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun message pour l'instant</p>
            <p className="text-sm mt-1">Sois le premier à écrire ! 💬</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.customerId === customerId;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-lg flex-shrink-0">
                  {msg.profilePhoto ? (
                    <img src={msg.profilePhoto} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    msg.avatar || '😊'
                  )}
                </div>
                
                {/* Contenu */}
                <div className={`max-w-[70%] ${isMe ? 'text-right' : ''}`}>
                  <div className={`flex items-center gap-2 mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {isMe ? 'Moi' : msg.displayName}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {isMe && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  
                  {/* Image si présente */}
                  {msg.type === 'photo' && msg.imageUrl && (
                    <div className="mb-2">
                      <img 
                        src={msg.imageUrl} 
                        alt="Shared" 
                        className="rounded-xl max-w-full h-auto max-h-48 object-cover cursor-pointer hover:opacity-90"
                        onClick={() => window.open(msg.imageUrl, '_blank')}
                      />
                    </div>
                  )}
                  
                  {/* 🎬 Vidéo si présente */}
                  {msg.type === 'video' && msg.videoUrl && (
                    <VideoMessage 
                      videoUrl={msg.videoUrl} 
                      duration={msg.duration}
                      isDarkMode={isDarkMode}
                    />
                  )}
                  
                  {/* Texte */}
                  {msg.text && (
                    <div className={`inline-block px-4 py-2 rounded-2xl ${
                      isMe
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-800 shadow-sm'
                    }`}>
                      {msg.text}
                    </div>
                  )}
                  
                  {/* Réactions */}
                  {msg.reactions && msg.reactions.length > 0 && (
                    <div className={`flex gap-1 mt-1 flex-wrap ${isMe ? 'justify-end' : ''}`}>
                      {msg.reactions.map((reaction, i) => (
                        <button
                          key={i}
                          onClick={() => handleAddReaction(msg.id, reaction.emoji)}
                          className={`px-2 py-0.5 rounded-full text-sm ${
                            isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                          }`}
                        >
                          {reaction.emoji} {reaction.by?.length > 1 && reaction.by.length}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Bouton ajouter réaction */}
                  <button
                    onClick={() => handleAddReaction(msg.id, REACTIONS[Math.floor(Math.random() * REACTIONS.length)])}
                    className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
                  >
                    + Réagir
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        {/* Barre de progression upload vidéo */}
        {uploadingVideo && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-purple-400" />
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Upload vidéo... {videoProgress}%
              </span>
            </div>
            <div className={`h-1 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <motion.div 
                className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
                initial={{ width: 0 }}
                animate={{ width: `${videoProgress}%` }}
              />
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          {/* Bouton photo */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleSendPhoto}
            accept="image/*"
            className="hidden"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto || uploadingVideo}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'} ${uploadingPhoto ? 'animate-pulse' : ''}`}
            data-testid="photo-button"
            title="Envoyer une photo"
          >
            {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>
          
          {/* 🎬 Bouton vidéo */}
          <input
            type="file"
            ref={videoInputRef}
            onChange={handleSendVideo}
            accept="video/*"
            className="hidden"
          />
          <button 
            onClick={() => videoInputRef.current?.click()}
            disabled={uploadingVideo || uploadingPhoto}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'} ${uploadingVideo ? 'animate-pulse' : ''}`}
            data-testid="video-button"
            title="Envoyer une vidéo (max 30s)"
          >
            {uploadingVideo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
          </button>
          
          {/* Input */}
          <div className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-full ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              placeholder="Écris ton message..."
              className={`flex-1 bg-transparent outline-none text-sm ${
                isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-500'
              }`}
              data-testid="message-input"
            />
            <button 
              className="text-gray-400 hover:text-gray-300"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            >
              <Smile className="w-5 h-5" />
            </button>
          </div>
          
          {/* Bouton envoyer */}
          <motion.button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            className={`p-3 rounded-full ${
              newMessage.trim() && !sending
                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="send-button"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </motion.button>
        </div>
        
        {/* Emoji picker simple */}
        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`mt-2 p-2 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white shadow-lg border'}`}
            >
              <div className="flex flex-wrap gap-2">
                {['😊', '😂', '😍', '🥰', '😎', '🔥', '❤️', '👏', '💯', '✨', '🎉', '🙈'].map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => {
                      setNewMessage(prev => prev + emoji);
                      setShowEmojiPicker(false);
                    }}
                    className="text-2xl hover:scale-125 transition-transform"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default TxtofsTab;
